import { WithAuthorization } from "../../../context";
import { Page } from "../../../components/Page";
import {
  Badge,
  Box,
  Button,
  Flex,
  Stack,
  Wrap,
  WrapItem,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "../../../utils";
import { hideAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { issbKeys, useDeleteEntity, useGetOrganizationEntities } from "../../../hooks/query";
import EditEntityModal from "./EditEntityModal";
import { prettyPrintCodeName } from "utils/text-utils";
import EntityUserModal from "./EntityUserModal";
import useAirUserRoles from "../../../utils/AuthUtils";
import EntitiesTable from "./EntitiesTable";
import PageHeader from "../../../components/text/PageHeader";
import { AButton } from "../../../components/A";

function EntityPage() {
  const { t } = useTranslation("translation", {
    keyPrefix: "",
  });

  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const toast = useToast();
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState();
  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const { isOpen: isEntityModalOpen, onOpen: onEntityModalOpen, onClose: onEntityModalClose } = useDisclosure();

  const { isOrgAdmin } = useAirUserRoles();

  const { isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useGetOrganizationEntities({
    onSuccess: (entities) => {
      setEntities(entities);
    },
    onError: (err) => handleErrors(err),
    cacheTime: 0,
    enabled: true,
  });

  const { mutate: deleteEntity } = useDeleteEntity({
    onSuccess: () => {
      showSuccessToast(toast, t("issb.entities.messages.deletedEntity"));
      queryClient.invalidateQueries(issbKeys.entity());
    },
    onError: (err) => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    },
  });

  const handleErrors = (error) => {
    let errorMessage = error.response?.data?.parameters?.message ?? error.message;
    dispatch(hideAppSpinner());
    showErrorToast(toast, errorMessage);
  };

  const handleAddNewEntity = () => {
    setSelectedEntity(null);
    onEntityModalOpen();
  };

  const closeEntityModal = () => {
    setSelectedEntity(null);
    onEntityModalClose();
    onUserModalClose();
  };

  const handleRowClick = (row) => {
    setSelectedEntity(row.original);
    onEntityModalOpen();
  };

  const handleManageUsers = (row) => {
    setSelectedEntity(row.original);
    onUserModalOpen();
  };

  function handleDeleteEntity(entityId) {
    if (window.confirm("Are you sure you want to delete this entity?")) {
      deleteEntity(entityId);
      onEntityModalClose();
    }
  }

  /** @type ColumnDef[] */
  const columns = [
    {
      header: "Name",
      accessorKey: "name",
      size: 180,
      cell: ({ row }) => (
        <Box
          cursor="pointer"
          onClick={() => handleRowClick(row)}
          _hover={{ color: "limeShade.600" }}
        >
          {row.original.name}
        </Box>
      ),
    },
    {
      header: "Sector",
      accessorFn: (row) => row.sectors?.map(prettyPrintCodeName).join(", "),
      cell: ({ row }) => (
        <Wrap spacing={1}>
          {row.original.sectors?.map((s) => (
            <WrapItem key={s}>
              <Badge borderRadius="md" px={2} py={0.5} fontSize="xs" colorScheme="gray" whiteSpace="normal" lineHeight="1.4">{prettyPrintCodeName(s)}</Badge>
            </WrapItem>
          ))}
        </Wrap>
      ),
    },
    {
      header: "Sub-Sector / Industry",
      accessorFn: (row) => row.subsectors?.map(prettyPrintCodeName).join(", ") || row.industry,
      cell: ({ row }) => {
        const items = row.original.subsectors?.length ? row.original.subsectors : (row.original.industry ? [row.original.industry] : []);
        return (
          <Wrap spacing={1}>
            {items.map((s) => (
              <WrapItem key={s}>
                <Badge borderRadius="md" px={2} py={0.5} fontSize="xs" colorScheme="gray" whiteSpace="normal" lineHeight="1.4">{prettyPrintCodeName(s)}</Badge>
              </WrapItem>
            ))}
          </Wrap>
        );
      },
    },
    {
      header: t("issb.entities.location"),
      accessorFn: (row) => row.place?.name || row.place?.countryIso || row.place?.region || row.location,
      size: 140,
      cell: ({ row, cell }) => (
        <Box
          cursor="pointer"
          onClick={() => handleRowClick(row)}
          _hover={{ color: "limeShade.600" }}
        >
          {cell.getValue()}
        </Box>
      ),
    },
    {
      header: "",
      id: "users",
      size: 100,
      cell: ({ row }) => (
        <AButton
          variant="link"
          onClick={(e) => { e.stopPropagation(); handleManageUsers(row); }}
          fontSize="xs"
          color="limeShade.600"
          fontWeight="normal"
          _hover={{ textDecoration: "underline" }}
        >
          Users →
        </AButton>
      ),
    },
  ];

  return (
    <Page>
      <Stack spacing={10} p={4} bg="pureWhite" marginLeft="15%" marginRight="15%">
        <Flex justify="space-between" align="flex-start">
          <PageHeader
            title="Entity Management"
            subTitle="Create and manage your organization's entities, their sectors, locations, and user assignments."
          />
          <Button variant="primary" borderRadius="full" onClick={handleAddNewEntity} flexShrink={0} mt={2}>
            + Add Entity
          </Button>
        </Flex>

        {isEntityModalOpen && (
          <EditEntityModal
            entity={selectedEntity}
            isOpen={isEntityModalOpen}
            onClose={closeEntityModal}
            onDelete={isOrgAdmin && selectedEntity?.id ? () => handleDeleteEntity(selectedEntity.id) : null}
          />
        )}

        {isUserModalOpen && (
          <EntityUserModal entity={selectedEntity} isOpen={isUserModalOpen} onClose={closeEntityModal} />
        )}

        <EntitiesTable
          data={entities}
          columns={columns}
          isLoading={isLoadingUsers || isFetchingUsers}
        />
      </Stack>
    </Page>
  );
}

export default WithAuthorization(EntityPage);
